import React, { useEffect, useRef, useState } from "react";
import { initialsFrom } from "../../lib/initials";
import { useAuth } from "../../providers/AuthProvider";

function moveFocus(list, step) {
  const items = Array.from(list.querySelectorAll('[role="menuitem"]'));
  const current = items.indexOf(document.activeElement);
  const next = (current + step + items.length) % items.length;
  items[next]?.focus();
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const displayName = user?.name || user?.email || "Usuário";
  const secondaryLine = user?.email !== displayName ? user?.email : null;

  const closeToTrigger = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;

    // No mousedown, não no click: fechar só no click deixaria o menu aberto
    // durante todo o gesto de pressionar fora dele.
    const closeOnOutsidePress = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") closeToTrigger();
    };

    document.addEventListener("mousedown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  useEffect(() => {
    if (open) listRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open]);

  const handleTriggerKeyDown = (event) => {
    if (event.key !== "ArrowDown") return;
    event.preventDefault();
    setOpen(true);
  };

  const handleListKeyDown = (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    moveFocus(listRef.current, event.key === "ArrowDown" ? 1 : -1);
  };

  return (
    <div className="usermenu" ref={rootRef}>
      {/* O nome fica dentro do menu; aqui ele é só o nome acessível do
          gatilho, que sem ele seria apenas as iniciais decorativas. */}
      <button
        className="usermenu-trigger"
        type="button"
        ref={triggerRef}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={displayName}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="avatar" aria-hidden="true">
          {initialsFrom(user)}
        </span>
      </button>

      {open && (
        <div className="menu-pop">
          {/* Fora do role="menu": um cabeçalho de identidade não é menuitem,
              e a ARIA de menu só admite itens dentro do contêiner. */}
          <div className="menu-head">
            <div className="user-name">{displayName}</div>
            {secondaryLine && <div className="user-mail">{secondaryLine}</div>}
          </div>

          <div
            className="menu-list"
            role="menu"
            aria-label="Conta"
            ref={listRef}
            onKeyDown={handleListKeyDown}
          >
            {/* Logout de um clique, sem window.confirm: o diálogo nativo
                bloqueia a thread e não é estilizável pelo DS (§4.10). */}
            <button className="menu-item" type="button" role="menuitem" onClick={signOut}>
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
